import React from 'react';
import { Box, Grid, Typography, Card, CardContent, Chip, Divider, List, ListItem, ListItemText, Avatar, Stack, LinearProgress, Pagination, TextField, Paper } from '@mui/material';
import { LayoutBaseDeDashboard } from '../../../shared/layouts';
import { useAppThemeContext } from '../../../shared/contexts';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { socket } from '../../../shared/services/api/websocket/websocket';
import {
  CheckCircleOutline as AprovadoIcon,
  HourglassEmpty as PendenteIcon,
  Cached as ProcessamentoIcon,
  DoneAll as ConcluidoIcon,
  Cancel as RejeitadoIcon,
  Person as SolicitanteIcon,
  Event as DataIcon
} from '@mui/icons-material';
import { BasicSelectFiltro } from '../../../shared/forms';
import { Environment } from '../../../shared/environment';

const STATUS_CONFIG_CACHE: { [key: number]: any } = {
  2: {
    text: 'Recebido',
    color: '#2ecc71',
    bgColor: 'rgba(76, 175, 80, 0.2)',
    icon: <ProcessamentoIcon fontSize="small" />
  },
  3: {
    text: 'Visualizado',
    color: '#f1c40f',
    bgColor: 'rgba(212, 175, 55, 0.1)',
    icon: <AprovadoIcon fontSize="small" />
  },
  4: {
    text: 'Separando',
    color: '#3498db',
    bgColor: 'rgba(33, 150, 243, 0.2)',
    icon: <ConcluidoIcon fontSize="small" />
  },
  5: {
    text: 'Finalizado',
    color: '#1e843dff',
    bgColor: 'rgba(39, 255, 50, 0.2)',
    icon: <RejeitadoIcon fontSize="small" />
  }
};
const DEFAULT_STATUS_CONFIG = {
  text: 'Desconhecido',
  color: '#9E9E9E',
  bgColor: 'rgba(158, 158, 158, 0.2)',
  icon: null
};
const dateFormatCache = new Map<string, string>();
const shortDateFormatCache = new Map<string, string>();
const gerarCodigoSimples = (uuid: string): number => {
  const last = uuid.split('-').pop()!;
  return parseInt(last.substring(0, 4), 16);
};
export const DashboardSolicitacao = () => {
  const [totalCount, setTotalCount] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { themeName } = useAppThemeContext();
  const temaSistema = themeName === 'dark' ? 'dark' : 'light';
  const hoje = new Date();
  const primeiroDiaMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
  const formatarInputDate = (data: Date) =>
    data.toISOString().split('T')[0];
  const [datainicial, setDataInicial] = useState(
    searchParams.get('datainicial') || formatarInputDate(primeiroDiaMes)
  );
  const [datafinal, setDataFinal] = useState(
    searchParams.get('datafinal') || formatarInputDate(hoje)
  );
  const tipoInitial = searchParams.get('tipo') || 'aberto';
  const pagina = useMemo(() => Number(searchParams.get('pagina') || '1'), [searchParams]);
  const [tipoSelect, setTipoSelect] = useState(tipoInitial);
  const [loading, setLoading] = useState(false);
  const [solicitacoes, setSolicitacoes] = useState<any[]>([]);
  const sortSolicitacoes = useCallback((data: any[]) => {
    return [...data].sort(
      (a, b) => new Date(b.dataenvio).getTime() - new Date(a.dataenvio).getTime()
    );
  }, []);
  const handleAtualizacao = useCallback((response?: { data?: any[]; params?: any; total?: number }) => {
    if (!response || !Array.isArray(response.data)) return;
    if (response.data.length === 0 || response.params?.id !== id) return;
    const sorted = sortSolicitacoes(response.data);
    setSolicitacoes(sorted);
    setLoading(false);
  }, [id, sortSolicitacoes]);
  useEffect(() => {
    socket.on('solicitacoes', handleAtualizacao);
    return () => {
      socket.off('solicitacoes', handleAtualizacao);
    };
  }, [handleAtualizacao]);
  useEffect(() => {
    const params: any = {
      id,
      page: pagina,
      limit: Environment.LIMITE_DE_LINHAS,
      tipo: tipoSelect
    };
    if (tipoSelect === 'fechado') {
      params.datainicial = datainicial;
      params.datafinal = datafinal;
    }
    setLoading(true);
    socket.emit('solicitacoes', params, (response: { data: any[]; total: number }) => {
      const sorted = sortSolicitacoes(response.data);
      setSolicitacoes(sorted);
      setTotalCount(response.total);
      setLoading(false);
    });
  }, [id, pagina, tipoSelect, datainicial, datafinal, sortSolicitacoes]);
  const formatDate = useCallback((dateString: string): string => {
    if (dateFormatCache.has(dateString)) return dateFormatCache.get(dateString)!;
    try {
      const formatted = format(parseISO(dateString), 'dd/MM/yyyy HH:mm', { locale: ptBR });
      dateFormatCache.set(dateString, formatted);
      return formatted;
    } catch {
      return dateString;
    }
  }, []);
  const formatShortDate = useCallback((dateString: string): string => {
    if (shortDateFormatCache.has(dateString)) return shortDateFormatCache.get(dateString)!;
    try {
      const formatted = format(parseISO(dateString), 'dd/MM', { locale: ptBR });
      shortDateFormatCache.set(dateString, formatted);
      return formatted;
    } catch {
      return dateString;
    }
  }, []);
  const getStatusConfig = useCallback(
    (status: number) => STATUS_CONFIG_CACHE[status] || DEFAULT_STATUS_CONFIG,
    []
  );
  const handlePageChange = (_: any, newPage: number) => {
    const params: any = { pagina: newPage.toString(), tipo: tipoSelect };
    if (tipoSelect === 'fechado') {
      params.datainicial = datainicial;
      params.datafinal = datafinal;
    }
    setSearchParams(params, { replace: true });
  };
  const handleTipoChange = (novoTipo: string | undefined) => {
    const tipo = novoTipo ?? 'aberto';
    setTipoSelect(tipo);
    const params: any = { pagina: '1', tipo };
    if (tipo === 'fechado') {
      params.datainicial = datainicial;
      params.datafinal = datafinal;
    }
    setSearchParams(params, { replace: true });
  };
  const handleChangeDataInicial = (e: any) => {
    const val = e.target.value;
    setDataInicial(val);

    setSearchParams({
      pagina: '1',
      tipo: tipoSelect,
      datainicial: val,
      datafinal
    });
  };
  const handleChangeDataFinal = (e: any) => {
    const val = e.target.value;
    setDataFinal(val);
    setSearchParams({
      pagina: '1',
      tipo: tipoSelect,
      datainicial,
      datafinal: val
    });
  };
  const SolicitacaoItem = React.memo(({ solicitacao }: { solicitacao: any }) => {
    const statusConfig = getStatusConfig(solicitacao.status);
    const codigo = gerarCodigoSimples(solicitacao.idsolicitacao);
    return (
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderLeft: `4px solid ${statusConfig.color}`,
            boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
            minHeight: 250,
            cursor: 'pointer'
          }}
          onClick={() => navigate(`/solicitacao/detalhes/${solicitacao.idsolicitacao}`)}
        >
          <Box
            sx={{
              p: 1.5,
              backgroundColor: statusConfig.bgColor,
              borderBottom: `1px solid ${statusConfig.color}30`
            }}
          >
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1}
              sx={{ flexWrap: 'wrap' }}
            >
              <SolicitanteIcon color="action" fontSize="small" />

              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  flexGrow: 1,
                  minWidth: '100px'
                }}
              >
                {solicitacao.solicitante} - {codigo}
              </Typography>

              {solicitacao.impresso === true && (
                <Chip
                  label="Impresso"
                  size="small"
                  sx={{
                    backgroundColor: 'transparent',
                    border: '1px solid currentColor',
                    color: '#6c757d',
                    fontSize: '0.65rem',
                    fontWeight: 'bold',
                    height: 20,
                    mt: 0.5   
                  }}
                />
              )}
            </Stack>
          </Box>

          <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="caption" color="text.secondary">
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <DataIcon color="action" fontSize="inherit" />
                  <span>{formatDate(solicitacao.dataenvio)}</span>
                </Stack>
              </Typography>
              <Chip
                label={statusConfig.text}
                size="small"
                icon={statusConfig.icon || undefined}
                sx={{
                  backgroundColor: `${statusConfig.color}30`,
                  color: statusConfig.color,
                  fontWeight: 'bold',
                  fontSize: '0.65rem',
                  '& .MuiChip-icon': { color: statusConfig.color, ml: 0.5 }
                }}
              />
            </Stack>
            <Divider sx={{ my: 1 }} />
            <List dense sx={{ py: 0 }}>
              {solicitacao.itens.map((item: any) => (
                <ListItem
                  key={item.iditem}
                  sx={{
                    px: 0,
                    py: 0.25,
                    borderLeft: item.urgente ? '2px solid #F44336' : 'none'
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: item.urgente ? 'error.main' : 'primary.main',
                      width: 22,
                      height: 22,
                      fontSize: 10,
                      mr: 1
                    }}
                  >
                    {parseFloat(String(item.qtd).replace(',', '.')).toLocaleString('pt-BR', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    })}
                  </Avatar>
                  <ListItemText
                    primary={<Typography variant="caption">{item.descricao}</Typography>}
                    secondary={
                      <Typography variant="caption" color="text.secondary">
                        {item.referencia} • {formatShortDate(item.datalan)}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Grid>
    );
  });
  const renderContent = useMemo(() => {
    if (loading) {
      return (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <LinearProgress style={{ width: '100%' }} />
          </Box>
        </Grid>
      );
    }
    if (solicitacoes.length === 0) {
      return (
        <Grid item xs={12}>
          <Box display="flex" justifyContent="center" alignItems="center" height={200}>
            <Typography>{Environment.LISTAGEM_VAZIA}</Typography>
          </Box>
        </Grid>
      );
    }
    return solicitacoes.map((sol) => (
      <SolicitacaoItem key={sol.idsolicitacao} solicitacao={sol} />
    ));
  }, [loading, solicitacoes]);
  return (
    <LayoutBaseDeDashboard>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          gap: 3,
          backgroundColor: temaSistema === 'dark' ? '#111' : '#ffffff',
          border: temaSistema === 'dark'
            ? '1px solid rgba(255,255,255,0.05)'
            : '1px solid rgba(0,0,0,0.08)',
          boxShadow: temaSistema === 'dark'
            ? '0 2px 8px rgba(0,0,0,0.3)'
            : '0 2px 6px rgba(0,0,0,0.1)',
        }}
      >
        <Box sx={{ minWidth: 220 }}>
          <Typography
            sx={{
              mb: 0.5,
              fontSize: '0.75rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              opacity: 0.6,
            }}
          >
            Filtrar tipo
          </Typography>

          <BasicSelectFiltro
            data={[
              { nameBD: 'aberto', descricao: 'Aberto' },
              { nameBD: 'fechado', descricao: 'Fechado' }
            ]}
            label=""
            value={tipoSelect}
            onChange={handleTipoChange}
            borderRadius={2}
          />
        </Box>
        {tipoSelect === 'fechado' && (
          <Box
            sx={{
              display: 'flex',
              gap: 3,
              flexWrap: 'wrap',
              alignItems: 'flex-end'
            }}
          >
            <Box>
              <Typography
                sx={{
                  mb: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  opacity: 0.6,
                }}
              >
                Data Inicial
              </Typography>
              <TextField
                type="date"
                value={datainicial}
                onChange={handleChangeDataInicial}
                size="small"
                sx={{
                  width: 170,
                  background: temaSistema === 'dark' ? '#0a0a0a' : '#fafafa',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
            </Box>
            <Box>
              <Typography
                sx={{
                  mb: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  opacity: 0.6,
                }}
              >
                Data Final
              </Typography>
              <TextField
                type="date"
                value={datafinal}
                onChange={handleChangeDataFinal}
                size="small"
                sx={{
                  width: 170,
                  background: temaSistema === 'dark' ? '#0a0a0a' : '#fafafa',
                  borderRadius: 2,
                  '& .MuiOutlinedInput-root': { borderRadius: 2 }
                }}
              />
            </Box>
          </Box>
        )}
      </Paper>
      <Grid container spacing={2}>
        {renderContent}
      </Grid>
      {totalCount > 0 && (
        <Box mt={4} display="flex" justifyContent="center">
          <Pagination
            count={Math.ceil(totalCount / Environment.LIMITE_DE_LINHAS)}
            page={pagina}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}
    </LayoutBaseDeDashboard>
  );
};
